import { Megaphone } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, hlaskaZChyby } from '../api/client.js';
import { useVybranaZakazka } from '../lib/globalniZakazka.js';
import type { Vyzva, Zakazka } from '../api/types.js';
import { useAuth } from '../auth/AuthContext.js';
import {
  Badge,
  Chyba,
  EmptyState,
  Input,
  Nacitani,
  Textarea,
  VelkeTlacitko,
} from '../components/ui.js';
import { formatDatum } from '../lib/format.js';
import { VYZVA_STAV_BARVA, VYZVA_STAV_LABEL } from '../lib/labels.js';

/** Je termín výzvy po dnešku? (zvýraznění prošlých termínů — UX audit B8.4) */
function poTerminu(termin: string): boolean {
  const t = new Date(termin);
  if (Number.isNaN(t.getTime())) return false;
  const dnes = new Date();
  dnes.setHours(0, 0, 0, 0);
  return t < dnes;
}

/**
 * Výzvy (checklist E1–E2) — společné pro terén i kancelář: seznam výzev se
 * stavem otevřeno/vyjádřeno; vyjádření za TDI zapisuje stavbyvedoucí
 * (denik.write) nebo kancelář (projekty.write) — TDI nemá login (fáze 2).
 */
export function VyzvyPage() {
  const { maPravo } = useAuth();
  const [chyba, setChyba] = useState<string | null>(null);
  const smiVyjadrit = maPravo('denik.write') || maPravo('projekty.write');

  // Seznam se řídí globálním přepínačem zakázky; „všechny" na jedno kliknutí.
  const { data: zakazky } = useQuery({
    queryKey: ['zakazky'],
    queryFn: () => api.get<Zakazka[]>('/api/projects'),
  });
  const { vybranaId } = useVybranaZakazka(zakazky);
  const [vsechny, setVsechny] = useState(false);
  useEffect(() => setVsechny(false), [vybranaId]);
  const filtr = !vsechny && vybranaId ? `?zakazkaId=${vybranaId}` : '';
  const {
    data: vyzvy,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['vyzvy', filtr],
    queryFn: () => api.get<Vyzva[]>(`/api/vyzvy${filtr}`),
  });

  // Otevřené řadíme podle termínu (prošlé/nejbližší nahoru — blueprint 16 §10);
  // výzvy bez termínu na konec.
  const otevrene = (vyzvy ?? [])
    .filter((v) => v.stav === 'vyzvano')
    .sort((a, b) => {
      if (!a.termin) return b.termin ? 1 : 0;
      if (!b.termin) return -1;
      return new Date(a.termin).getTime() - new Date(b.termin).getTime();
    });
  const ostatni = (vyzvy ?? []).filter((v) => v.stav !== 'vyzvano');

  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-xl font-bold">
        Výzvy {otevrene.length > 0 ? `(${otevrene.length} otevřených)` : ''}
      </h1>
      {isLoading && <Nacitani />}
      {error ? <Chyba>{hlaskaZChyby(error)}</Chyba> : null}
      {chyba ? <Chyba>{chyba}</Chyba> : null}
      {vyzvy && vyzvy.length === 0 && (
        <EmptyState
          ikona={<Megaphone size={22} strokeWidth={1.75} />}
          titulek={filtr ? 'Vybraná zakázka nemá výzvy' : 'Žádné výzvy'}
          popis="Výzvu TDI založíte z deníku tlačítkem „VÝZVA TDI“ — třeba den před zakrýváním konstrukce."
          akce={
            filtr ? (
              <button
                type="button"
                className="font-semibold text-primary underline underline-offset-2"
                onClick={() => setVsechny(true)}
              >
                Zobrazit všechny zakázky
              </button>
            ) : undefined
          }
        />
      )}
      <ul className="flex flex-col gap-3">
        {[...otevrene, ...ostatni].map((v) => (
          <li key={v.id} className="rounded-xl border border-border bg-surface p-4 shadow-sm">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="text-base font-semibold">
                  {v.adresatUser?.jmeno ?? v.adresatRole ?? '—'}
                  {v.termin ? (
                    <span
                      className={`font-normal ${
                        v.stav === 'vyzvano' && poTerminu(v.termin)
                          ? 'font-semibold text-error-text'
                          : 'text-text-secondary'
                      }`}
                    >
                      {' '}
                      · termín {formatDatum(v.termin)}
                      {v.stav === 'vyzvano' && poTerminu(v.termin) ? ' (po termínu)' : ''}
                    </span>
                  ) : null}
                </p>
                <p className="text-base whitespace-pre-wrap">
                  {v.predmetTyp === 'prejimka' && (
                    <span className="mr-1.5 rounded bg-warning-bg px-1.5 py-0.5 text-xs font-bold text-warning-text">
                      PŘEJÍMKA
                    </span>
                  )}
                  {v.text}
                </p>
                <p className="text-sm text-text-secondary">
                  vyzval {v.vyzval?.jmeno ?? '—'} · {formatDatum(v.createdAt)}
                </p>
              </div>
              <Badge barva={VYZVA_STAV_BARVA[v.stav]}>{VYZVA_STAV_LABEL[v.stav]}</Badge>
            </div>
            {v.vyjadreni ? (
              <p className="mt-2 rounded-lg bg-success-bg p-2 text-base">
                Vyjádření ({formatDatum(v.vyjadrenoAt)}): {v.vyjadreni}
              </p>
            ) : null}
            {v.stav === 'vyzvano' && smiVyjadrit ? (
              <VyjadreniForm vyzvaId={v.id} naChybu={setChyba} />
            ) : null}
          </li>
        ))}
      </ul>
      {filtr && (vyzvy ?? []).length > 0 && (
        <button
          type="button"
          className="self-start text-sm font-semibold text-primary underline underline-offset-2"
          onClick={() => setVsechny(true)}
        >
          Zobrazit výzvy všech zakázek
        </button>
      )}
    </div>
  );
}

/** E2: vyjádření se eviduje s datem a jménem vyjadřující se osoby (TDI). */
function VyjadreniForm({
  vyzvaId,
  naChybu,
}: {
  vyzvaId: string;
  naChybu: (ch: string | null) => void;
}) {
  const queryClient = useQueryClient();
  const [oteviram, setOteviram] = useState(false);
  const [vyjadreni, setVyjadreni] = useState('');
  const [jmeno, setJmeno] = useState('');

  const odeslat = useMutation({
    mutationFn: () =>
      api.post<Vyzva>(`/api/vyzvy/${vyzvaId}/vyjadreni`, {
        vyjadreni,
        ...(jmeno.trim() ? { jmeno: jmeno.trim() } : {}),
      }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['vyzvy'] }),
    onError: (err) => naChybu(hlaskaZChyby(err)),
  });

  if (!oteviram) {
    return (
      <button
        type="button"
        className="mt-2 min-h-11 font-semibold text-primary underline underline-offset-2"
        onClick={() => setOteviram(true)}
      >
        Zapsat vyjádření (za TDI)
      </button>
    );
  }
  return (
    <div className="mt-3 flex flex-col gap-2">
      <Input
        aria-label="Kdo se vyjádřil"
        placeholder="Kdo se vyjádřil — např. Ing. Beneš (TDI)"
        value={jmeno}
        onChange={(e) => setJmeno(e.target.value)}
      />
      <Textarea
        aria-label="Vyjádření"
        rows={3}
        placeholder="Text vyjádření (z mailu / papíru)…"
        value={vyjadreni}
        onChange={(e) => setVyjadreni(e.target.value)}
      />
      <VelkeTlacitko
        disabled={!vyjadreni.trim() || odeslat.isPending}
        onClick={() => odeslat.mutate()}
      >
        {odeslat.isPending ? 'Ukládám…' : 'Zaevidovat vyjádření'}
      </VelkeTlacitko>
    </div>
  );
}
